import Axios, { type AxiosRequestConfig } from "axios";

export type RequestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

function isParamsMethod(method: RequestMethod) {
  return method === "GET" || method === "DELETE";
}

export type ErrorType = "response" | "network" | "configuration";

export type ResponseRequestError = {
  type: "response";
  payload: any;
  status: number;
  error: unknown;
};

export type NetworkRequestError = {
  type: "network";
  error: unknown;
};

export type ConfigurationRequestError = {
  type: "configuration";
  error: unknown;
};

export type RequestError =
  | ResponseRequestError
  | NetworkRequestError
  | ConfigurationRequestError;

export type FetcherOptions = {
  headers?: Record<string, string>;
};

export type RequestOptions = {
  method: RequestMethod;
  pathname: string;
  data?: object;
  headers?: Record<string, string>;
};

export function createFetcher(baseURL: string, options: FetcherOptions = {}) {
  function mergeHeaders(headers: Record<string, string> | undefined) {
    return {
      ...options.headers,
      ...headers,
    };
  }

  return async function fetch<T = unknown>(options: RequestOptions) {
    const { method, pathname, data, headers } = options;

    const url = baseURL + pathname;

    try {
      const config: AxiosRequestConfig = {
        method: method,
        url: url.toString(),
        headers: mergeHeaders(headers),
      };

      if (data) {
        if (isParamsMethod(method)) {
          config.params = data;
        } else {
          config.data = data;
        }
      }

      const response = await Axios(config);

      return response.data as T;
    } catch (error: any) {
      if (Axios.isAxiosError(error)) {
        if (error.response) {
          throw {
            type: "response",
            payload: error.response.data,
            status: error.response.status,
            error: error.response.data,
          } as ResponseRequestError;
        } else if (error.request) {
          throw {
            type: "network",
            error: error.request,
          } as NetworkRequestError;
        }
      }

      throw {
        type: "configuration",
        error: error,
      } as ConfigurationRequestError;
    }
  };
}
